import React from 'react';
import { MagicModel } from '@/types/MagicModel';
import { StyledSettingsSidebar } from './styles';
import { AllFlowNodes, NodeTypeMap } from '../Nodes/types';
import ButtonSettings from './SettingsComponents/ButtonSettings';
import BuzzerSettings from './SettingsComponents/BuzzerSettings';
import GeneralSettings from './SettingsComponents/GeneralSettings';
import NFCSettingsAll from './SettingsComponents/NFCSettingsAll';
import ShowSlideSettings from './SettingsComponents/ShowSlideSettings';
import { GeneralSettingsType } from './types';
import SwitchLanguageSettings from './SettingsComponents/SwitchLanguageSettings';
import StartHomeTimerSettings from './SettingsComponents/StartHometimerSettings';
import ToggleLEDSettings from './SettingsComponents/ToggleLEDSettings';
import TouchSettings from './SettingsComponents/TouchSettings';
import DispatchCustomEventSettings from './SettingsComponents/DispatchCustomEventSettings';
import TurnOnLEDSettings from './SettingsComponents/TurnOnLEDSettings';
import TurnOffLEDSettings from './SettingsComponents/TurnOffLEDSettings';
import CustomCodeSettings from './SettingsComponents/CustomCodeSettings';
import TrihowSpecificSettings from './SettingsComponents/TrihowSpecificSettings';

function SettingsSidebar({
  selectedNode,
  settings,
  setSettings,
  updateNodeData,
  magicModels,
  selectedSensors,
  setSelectedSensors,
  isSaving,
  isViewingCode,
}: {
  selectedNode: AllFlowNodes | null;
  settings: GeneralSettingsType;
  setSettings: React.Dispatch<React.SetStateAction<GeneralSettingsType>>;
  updateNodeData: (id: string, newData: any) => void;
  magicModels: MagicModel[];
  selectedSensors: {
    buttons: string[];
    buzzers: string[];
    trihows: string[];
  };
  setSelectedSensors: React.Dispatch<
    React.SetStateAction<{
      buttons: string[];
      buzzers: string[];
      trihows: string[];
    }>
  >;
  isSaving: boolean;
  isViewingCode: boolean;
}) {
  // This component renders the settings sidebar depending on the selected node.
  return (
    <StyledSettingsSidebar data-testid="settings-sidebar">
      {(!selectedNode || isViewingCode) && (
        <GeneralSettings
          settings={settings}
          setSettings={setSettings}
          disabled={isSaving}
        />
      )}
      {!isViewingCode &&
        selectedNode &&
        (() => {
          switch (selectedNode.type) {
            case 'sensor-nfcAll':
              return (
                <NFCSettingsAll
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={selectedNode as NodeTypeMap['sensor-nfcAll']}
                  updateNodeData={updateNodeData}
                  disabled={isSaving}
                />
              );
            case 'sensor-button':
              return (
                <ButtonSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={selectedNode as NodeTypeMap['sensor-button']}
                  updateNodeData={updateNodeData}
                  selectedButtons={selectedSensors.buttons}
                  setSelectedSensors={setSelectedSensors}
                  disabled={isSaving}
                />
              );
            case 'sensor-buzzer':
              return (
                <BuzzerSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={selectedNode as NodeTypeMap['sensor-buzzer']}
                  updateNodeData={updateNodeData}
                  selectedBuzzers={selectedSensors.buzzers}
                  setSelectedSensors={setSelectedSensors}
                  disabled={isSaving}
                />
              );
            case 'action-showSingleScreen':
              return (
                <ShowSlideSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={
                    selectedNode as NodeTypeMap['action-showSingleScreen']
                  }
                  updateNodeData={updateNodeData}
                  disabled={isSaving}
                />
              );
            case 'sensor-nfcSpecific':
              return (
                <TrihowSpecificSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={
                    selectedNode as NodeTypeMap['sensor-nfcSpecific']
                  }
                  updateNodeData={updateNodeData}
                  selectedTrihows={selectedSensors.trihows}
                  setSelectedSensors={setSelectedSensors}
                  magicModels={magicModels}
                  disabled={isSaving}
                />
              );
            case 'sensor-touch':
              return (
                <TouchSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={selectedNode as NodeTypeMap['sensor-touch']}
                  updateNodeData={updateNodeData}
                  disabled={isSaving}
                />
              );
            case 'action-switchLanguage':
              return (
                <SwitchLanguageSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={
                    selectedNode as NodeTypeMap['action-switchLanguage']
                  }
                  updateNodeData={updateNodeData}
                  disabled={isSaving}
                />
              );
            case 'action-startHometimer':
              return (
                <StartHomeTimerSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={
                    selectedNode as NodeTypeMap['action-startHometimer']
                  }
                  updateNodeData={updateNodeData}
                  disabled={isSaving}
                />
              );
            case 'action-toggleLED':
              return (
                <ToggleLEDSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={selectedNode as NodeTypeMap['action-toggleLED']}
                  updateNodeData={updateNodeData}
                  disabled={isSaving}
                />
              );
            case 'action-turnOnLED':
              return (
                <TurnOnLEDSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={selectedNode as NodeTypeMap['action-turnOnLED']}
                  updateNodeData={updateNodeData}
                  disabled={isSaving}
                />
              );
            case 'action-turnOffLED':
              return (
                <TurnOffLEDSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={
                    selectedNode as NodeTypeMap['action-turnOffLED']
                  }
                  updateNodeData={updateNodeData}
                  disabled={isSaving}
                />
              );
            case 'action-dispatchCustomEvent':
              return (
                <DispatchCustomEventSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={
                    selectedNode as NodeTypeMap['action-dispatchCustomEvent']
                  }
                  updateNodeData={updateNodeData}
                  disabled={isSaving}
                />
              );
            case 'action-customCode':
              return (
                <CustomCodeSettings
                  key={selectedNode.id}
                  settings={settings}
                  selectedNode={
                    selectedNode as NodeTypeMap['action-customCode']
                  }
                  updateNodeData={updateNodeData}
                  disabled={isSaving}
                />
              );
            default:
              return null;
          }
        })()}
    </StyledSettingsSidebar>
  );
}

export default SettingsSidebar;
